// src/components/DisciplineAppealPanel.js
//
// T5 / D-19 (2026-10-15) - the discipline appeal ladder on the case page: the acts with their dates, and the clocks
// they start (src\data\disciplineAppealLadder.js decides both; this file draws and writes). Acts are appended to the
// case's appealActs[] with arrayUnion - never rewritten - by an officer of the case; the rules refuse the member's own
// account (ownerOpenEdit) and the disciplineAuditHistory trigger records every change.
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ACTS, ACT_KINDS, APPEAL_OUTCOMES, OUTCOME_LABELS, AWARD_OUTCOMES, AWARD_LABELS, appealClocks, awardCompliance, recordAct, sortActs } from '../data/disciplineAppealLadder';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };
const todayISO = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
const CLOCK_KEYS = ['appeal_filing', 'appeal_decision', 'non_acceptance', 'board_institution', 'expedited_conference', 'expedited_decision'];

function isOfficer(user) {
  return !!user && (user.isAdmin || user.role === 'admin' || user.role === 'local_chair' || user.role === 'general_chair');
}

export default function DisciplineAppealPanel({ disciplineCase, user }) {
  const k = disciplineCase || {};
  const [form, setForm] = useState({ kind: 'appealed', on: todayISO() });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  if (!k.id || !k.status || k.status === 'draft') return null;
  const clocks = appealClocks(k, todayISO());
  const acts = sortActs(k.appealActs);
  const owed = awardCompliance(k);   // T5 / D-21
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    const r = recordAct(form);
    if (!r.ok) { setMsg({ ok: false, text: r.errors.join(' ') }); return; }
    setBusy(true); setMsg(null);
    try {
      const stored = { ...r.record, recordedBy: (user && (user.name || user.email)) || '', recordedByUid: (auth.currentUser && auth.currentUser.uid) || '' };
      await updateDoc(doc(db, 'disciplineCases', k.id), {
        appealActs: arrayUnion(stored), updatedAt: serverTimestamp(),
        updatedBy: (auth.currentUser && auth.currentUser.uid) || '', _auditActor: (user && user.email) || 'unknown',
        _auditReason: 'Appeal act recorded: ' + ACTS[r.record.kind].label + ' ' + r.record.on,
      });
      setForm({ kind: form.kind, on: todayISO() });
      setMsg({ ok: true, text: 'Recorded.' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>The appeal - acts and time limits</div>
      <div style={{ color: C.accent, fontSize: 11, margin: '2px 0 8px' }}>
        These limits were read from the agreement and are NOT YET CONFIRMED by the subject-matter review. Check each one against the agreement before relying on it.
      </div>
      {clocks.map(c => (
        <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '4px 0', borderBottom: '1px dotted ' + C.line }}>
          <div>
            <span style={{ color: C.text }}>{c.label}</span> <span style={{ color: C.dim }}>({c.whose === 'carrier' ? 'the carrier\'s limit' : 'our limit'})</span>
            <div style={{ fontSize: 11, color: C.dim }}>{c.basis}{c.extendedTo ? ' Extended by agreement to ' + c.extendedTo + '.' : ''}</div>
          </div>
          <div style={{ whiteSpace: 'nowrap', color: !c.open ? C.dim : c.left === null ? C.dim : c.left < 0 ? C.bad : c.left <= 10 ? C.accent : C.ok }}>
            {c.due ? (c.open ? (c.left < 0 ? 'passed ' + c.due : 'due ' + c.due + ' (' + c.left + 'd)') : 'was due ' + c.due) : 'no date'}
            {!c.open && c.due ? ' - closed' : ''}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 10, fontSize: 12, color: C.text }}>
        {acts.length === 0 ? <div style={{ color: C.dim }}>No appeal act is on record.</div> : acts.map((a, i) => (
          <div key={i} style={{ padding: '3px 0' }}>
            <b>{a.on}</b> - {ACTS[a.kind] ? ACTS[a.kind].label : a.kind}
            {a.toOfficer ? ' to ' + a.toOfficer : ''}{a.receivedOn ? ' (received ' + a.receivedOn + ')' : ''}{a.outcome ? ': ' + (OUTCOME_LABELS[a.outcome] || a.outcome) : ''}{a.board ? ' - ' + a.board : ''}
            {a.agreedWith ? ' - agreed with ' + a.agreedWith + (a.newDue ? ', new date ' + a.newDue : '') : ''}
            {a.awardOutcome ? ': ' + (AWARD_LABELS[a.awardOutcome] || a.awardOutcome) + (a.awardNumber ? ' (' + a.awardNumber + ')' : '') + (a.remedy ? ' - remedy: ' + a.remedy : '') : ''}
            {a.kind === 'make_whole_paid' ? (a.amount === null || a.amount === undefined ? ' - amount not recorded' : ' - $' + Number(a.amount).toLocaleString()) : ''}
            {a.note ? <span style={{ color: C.dim }}> - {a.note}</span> : null}
          </div>
        ))}
      </div>
      {owed.length ? (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div style={{ color: C.text, fontWeight: 700 }}>What the award requires of the carrier{owed[0].flaggedNotComplied ? <span style={{ color: C.bad }}> - recorded as NOT complied with</span> : null}</div>
          {owed.map(o => <div key={o.key} style={{ color: o.done ? C.ok : C.accent }}>{o.done ? 'Done' : 'Still owed'} - {o.label}</div>)}
        </div>
      ) : null}
      {isOfficer(user) ? (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <select aria-label="Which appeal act to record" className="form-select" value={form.kind} onChange={e => set('kind', e.target.value)}>
            {ACT_KINDS.map(x => <option key={x} value={x}>{ACTS[x].label}</option>)}
          </select>
          <input aria-label="Date" className="form-input" type="date" value={form.on || ''} onChange={e => set('on', e.target.value)} />
          {form.kind === 'appealed' ? <input aria-label="Officer appealed to" className="form-input" placeholder="Officer appealed to" value={form.toOfficer || ''} onChange={e => set('toOfficer', e.target.value)} /> : null}
          {/* Forensic audit 2026-10-16: the carrier's receipt of the appeal, optional - a clock that runs from receipt counts from it. */}
          {form.kind === 'appealed' ? <label style={{ fontSize: 11, color: C.dim }}>Received by the carrier (optional) <input aria-label="Received on" className="form-input" type="date" value={form.receivedOn || ''} onChange={e => set('receivedOn', e.target.value)} /></label> : null}
          {form.kind === 'appeal_decided' ? (
            <select aria-label="Received by the carrier (optional)" className="form-select" value={form.outcome || ''} onChange={e => set('outcome', e.target.value)}>
              <option value="">Outcome...</option>
              {APPEAL_OUTCOMES.map(o => <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>)}
            </select>) : null}
          {form.kind === 'award_issued' ? (<>
            <select aria-label="Award outcome" className="form-select" value={form.awardOutcome || ''} onChange={e => set('awardOutcome', e.target.value)}>
              <option value="">Award...</option>{AWARD_OUTCOMES.map(o => <option key={o} value={o}>{AWARD_LABELS[o]}</option>)}
            </select>
            <input aria-label="Award number" className="form-input" placeholder="Award number" value={form.awardNumber || ''} onChange={e => set('awardNumber', e.target.value)} />
            <input aria-label="Remedy, in the award" className="form-input" placeholder="Remedy, in the award's words" value={form.remedy || ''} onChange={e => set('remedy', e.target.value)} />
          </>) : null}
          {form.kind === 'make_whole_paid' ? <input aria-label="Amount (blank if not known)" className="form-input" placeholder="Amount (blank if not known)" value={form.amount || ''} onChange={e => set('amount', e.target.value)} /> : null}
          {form.kind === 'board_instituted' ? <input aria-label="Board (NRAB, PLB, SBA)" className="form-input" placeholder="Board (NRAB, PLB, SBA)" value={form.board || ''} onChange={e => set('board', e.target.value)} /> : null}
          {form.kind === 'extended' ? (<>
            <input aria-label="Carrier officer who agreed" className="form-input" placeholder="Carrier officer who agreed" value={form.agreedWith || ''} onChange={e => set('agreedWith', e.target.value)} />
            <select aria-label="Which limit" className="form-select" value={form.clock || ''} onChange={e => set('clock', e.target.value)}>
              <option value="">Which limit...</option>
              {CLOCK_KEYS.map(x => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
            </select>
            <input aria-label="New due" className="form-input" type="date" value={form.newDue || ''} onChange={e => set('newDue', e.target.value)} />
          </>) : null}
          <input aria-label="Note (optional)" className="form-input" placeholder="Note (optional)" value={form.note || ''} onChange={e => set('note', e.target.value)} />
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Recording...' : 'Record act'}</button>
          {msg ? <span style={{ fontSize: 12, color: msg.ok ? C.ok : C.bad }}>{msg.text}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
